import { useState, useEffect, useMemo, useCallback } from "react";
import { Mui, MuiIcons } from '@osu/react-ui';
import { useDispatch } from "react-redux";
import {
    useLazyPersonSearchQuery
} from "../../services/graphql/api";
import { clear } from "../../services/graphql/slices/search-slice";
import ResultsGrid from "./ResultsGrid";

function PersonSearch() {
    const [searchValue, setSearchValue] = useState("");
    const [cachedSearchTerm, setCachedSearchTerm] = useState("");
    const [paginationModel, setPaginationModel] = useState({ pageIndex: 0, pageSize: 100 });
    const [sortModel, setSortModel] = useState([{ id: 'documentCreatedDatetime', desc: false }]);
    const dispatch = useDispatch();
    const [
        triggerQuicksearch,
        {
            isFetching: personLoading
        },
    ] = useLazyPersonSearchQuery();

    // Clear search results when component mounts
    useEffect(() => {
        dispatch(clear());
    }, [dispatch]);

    // Create search params similar to AdvancedSearch
    const searchParams = useMemo(() => {
        if (!cachedSearchTerm || cachedSearchTerm.trim() === "") {
            return null;
        }
        return {
            searchTerm: cachedSearchTerm,
            paginationModel,
            sortModel
        };
    }, [cachedSearchTerm, paginationModel, sortModel]);

    // Auto-trigger search when params change
    useEffect(() => {
        if (searchParams) {
            triggerQuicksearch({ input: searchParams });
        }
    }, [searchParams, triggerQuicksearch]);

    const clearSearch = useCallback(() => {
        setSearchValue(""); // Clear the input value
        setCachedSearchTerm(""); // Clear cached search term
        dispatch(clear()); // Clear the search results from Redux
        // Reset pagination when clearing
        setPaginationModel({ pageIndex: 0, pageSize: paginationModel.pageSize });
    }, [dispatch]);

    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        setSearchValue(value); // Update the local state
    }, []);

    const executeSearch = useCallback(() => {
        if (searchValue.trim() === "") {
            return; // Do not perform search if input is empty
        }
        
        // Reset pagination to first page when doing a new search
        setPaginationModel({ pageIndex: 0, pageSize: paginationModel.pageSize });
        
        // Set the cached search term - this will trigger the useEffect above
        setCachedSearchTerm(searchValue.trim());
    }, [searchValue]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter") {
            executeSearch(); // Trigger search when Enter key is pressed
        }
    }, [executeSearch]);

    return (
        <Mui.Box>
            <Mui.Box sx={{ display: "flex", alignItems: "center" }}>
                <Mui.TextField
                    id="search-input"
                    label={"Applicant Id, Last Name, Name.# or Email"}
                    value={searchValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown} // Handle Enter key press
                    style={{ margin: "0.75rem 1rem 0.75rem 0", width: "89%" }}
                    variant="outlined"
                    InputProps={{
                        endAdornment:
                            <Mui.InputAdornment position="end">
                                <Mui.IconButton
                                    aria-label={"Search for reviewer or applicant button"}
                                    onClick={executeSearch} // Trigger search on icon click
                                    edge="end"
                                >
                                    {<MuiIcons.Search />}
                                </Mui.IconButton>
                            </Mui.InputAdornment>,
                    }}
                />
                <Mui.Button
                    variant="text"
                    color="secondary"
                    onClick={clearSearch} // Clear the input and notify parent
                    style={{ marginLeft: "1rem" }}
                >
                    Clear
                </Mui.Button>
            </Mui.Box>
            {personLoading && <Mui.LinearProgress className="margin-top-3" />}
            <ResultsGrid 
                isPersonSearch={true}
                searchParams={searchParams}
                clearSearchCriteria={clearSearch}
                clearGrid={clearSearch}
                paginationModel={paginationModel}
                sortModel={sortModel}
                setPaginationModel={setPaginationModel}
                setSortModel={setSortModel}
            />
        </Mui.Box>
    );
}

export default PersonSearch;